import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { atlediApi } from '@/features/insights/atlediApi';
import { ATLEDI_ENABLED } from '@/features/ai-assistant/atlediAvailability';

interface AtlediWelcomeHeroProps {
  brandName?: string;
  firstName?: string;
}

/**
 * PRD Xotski v2.0 §7.1 — Atledi welcome hero on Entity Home.
 *
 * The PRD specifies a context-aware, bilingual, soft-CTA greeting from
 * Atledi (Anthropic-backed) generated fresh per page load, cached 4h.
 * The Anthropic call lives in the backend AtlediService (Phase 4); until
 * that endpoint ships, the hero renders a deterministic local greeting
 * pulled from a small bank chosen by time-of-day. The shape, copy length
 * and CTA position match the PRD spec so the swap is one-line later.
 */
export function AtlediWelcomeHero({
  brandName,
  firstName,
}: AtlediWelcomeHeroProps) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const locale: 'en' | 'es' = language === 'es' ? 'es' : 'en';

  // PRD §16 — live greeting from /atledi/welcome (4h cached). Falls back
  // to the deterministic local bank when the backend has no Anthropic
  // key — same shape, same copy length, same swap point.
  const live = useQuery({
    queryKey: ['atledi-welcome', brandName ?? '', locale],
    queryFn: () => atlediApi.welcome({ brandName, locale }),
    // Nothing is fetched while Atledi is unreleased — the callout below is
    // static, so there is no greeting to ask for.
    enabled: ATLEDI_ENABLED,
    // 4h staleTime mirrors the backend cache TTL so React Query doesn't
    // refetch redundantly.
    staleTime: 4 * 60 * 60 * 1000,
    // Greeting is non-critical: never spinner the dashboard for it.
    retry: false,
  });

  const localGreeting = useMemo(
    () => buildLocalGreeting(t, { brandName, firstName }),
    [t, brandName, firstName],
  );
  const greeting = live.data?.greeting ?? localGreeting;

  // Task 8.3 — on home Atledi is a guiding callout, not a panel. Until it
  // ships it is shown deliberately inert: visible so people know where it will
  // live, dashed and muted so nobody waits for an answer that isn't coming.
  if (!ATLEDI_ENABLED) {
    return (
      <Card className="border-dashed border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] p-4">
        <div className="flex items-start gap-3">
          <div className="grid h-8 w-8 flex-none place-items-center rounded-full bg-[var(--color-bg-surface)] text-[var(--color-fg-subtle)]">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
              {t('entityHome.atledi.label')}
              <span className="rounded-full border border-[var(--color-border-strong)] px-1.5 py-0.5 text-[9px] font-bold normal-case tracking-normal">
                {t('assistant.comingSoon')}
              </span>
            </p>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              {t('entityHome.atledi.comingSoonBody')}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="overflow-hidden border-[var(--color-accent-ink)]/30 bg-gradient-to-br from-[var(--color-accent-08)] via-[var(--color-bg-surface)] to-[var(--color-bg-surface)] p-6"
    >
      <div className="flex items-start gap-4">
        <div className="grid h-10 w-10 flex-none place-items-center rounded-full bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-accent-ink)]">
            {t('entityHome.atledi.label')}
          </p>
          <p className="mt-1 text-lg leading-relaxed text-[var(--color-fg-primary)]">
            {greeting}
          </p>
        </div>
      </div>
    </Card>
  );
}

function buildLocalGreeting(
  t: (key: string) => string,
  ctx: { brandName?: string; firstName?: string },
): string {
  const hour = new Date().getHours();
  const slot =
    hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const subject = ctx.firstName ?? ctx.brandName ?? t('entityHome.atledi.youFallback');
  const template = t(`entityHome.atledi.greeting.${slot}`);
  return template.replace('{name}', subject);
}
